import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import CreateProjectDropdown from '../DropdownStyle/CreateProjectDropdown';

const PillButton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 9999px;
  font-size: 12px;
  user-select: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: rgb(245, 240, 236);
  color: rgb(107, 74, 54);
  font-weight: 500;
  border: 1px solid transparent;

  &:hover {
    background-color: rgb(238, 230, 223);
  }

  svg {
    width: 12px;
    height: 12px;
    color: rgb(156, 123, 103);
    flex-shrink: 0;
  }

  .count {
    color: rgb(79, 52, 35);
    font-weight: 600;
    margin-right: 2px;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: white;
  border: 1px solid #C2CCE1;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  min-width: 240px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  ::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border-bottom: 1px solid #F0F0F0;
  background-color: #FAFAFA;

  svg {
    width: 14px;
    height: 14px;
    color: rgb(156, 123, 103);
    flex-shrink: 0;
  }

  .header-count {
    color: rgb(79, 52, 35);
    font-weight: 600;
    font-size: 12px;
  }

  .header-label {
    color: rgb(107, 74, 54);
    font-weight: 500;
    font-size: 12px;
  }
`;

const Dot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: rgb(156, 123, 103);
  flex-shrink: 0;
`;


const OptionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #F0F0F0;
  gap: 8px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #F9F9F9;
  }

  label {
    cursor: pointer;
    flex: 1;
    margin: 0;
    font-size: 12px;
    color: rgb(107, 74, 54);
  }
`;

const Container = styled.div`
  position: relative;
  display: inline-block;
  width: ${({ width }) => width || '100%'};
`;

/**
 * A dropdown component with checkbox selection in pill style
 * @param {Object} props Component props
 * @param {Array} props.options Array of options with label and value
 * @param {Array} props.selectedOptions Currently selected options
 * @param {Function} props.onChange Callback when selection changes
 * @param {string} props.placeholder Placeholder text when nothing is selected
 * @param {Object} props.icon Icon SVG component or element
 * @param {boolean} props.opened Whether the dropdown is open (controlled externally)
 * @param {Function} props.onClick Callback when dropdown is clicked
 * @param {string} props.countLabel Label for selected count
 * @param {string} props.width Width of the dropdown
 */
const TableCustomCheckboxDropdownPill = ({
  options = [],
  selectedOptions = [],
  onChange,
  placeholder = 'Select options',
  icon = null,
  opened = false,
  onClick,
  countLabel = 'items_selected',
  width,
}) => {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);

  // Handle click on an option
  const handleOptionSelection = (option, e) => {
    if (e) {
      e.stopPropagation();
    }

    const isSelected = selectedOptions.some(o => o.value === option.value);
    let newSelection;

    if (isSelected) {
      newSelection = selectedOptions.filter(o => o.value !== option.value);
    } else {
      newSelection = [...selectedOptions, option];
    }

    onChange(newSelection);
  };

  // Handle click on the button
  const handleButtonClick = e => {
    e.preventDefault();
    e.stopPropagation();
    onClick && onClick();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        opened
      ) {
        onClick && onClick();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [opened, onClick]);

  return (
    <Container width={width} ref={dropdownRef}>
      <PillButton onClick={handleButtonClick}>
        {icon && icon}
        {selectedOptions.length > 0 ? (
          <>
            <span className="count">{selectedOptions.length}</span>
            <span>{t(countLabel)}</span>
          </>
        ) : (
          <span>{t(placeholder)}</span>
        )}
      </PillButton>

      {opened && (
        <DropdownMenu>
          {selectedOptions.length > 0 && (
            <DropdownHeader>
              {icon && icon}
              <span className="header-count">{selectedOptions.length}</span>
              <span className="header-label">{t(countLabel)}</span>
            </DropdownHeader>
          )}
          {options.map(option => (
            <OptionItem
              key={option.value}
              onClick={e => handleOptionSelection(option, e)}
            >
              <Dot />
              <label htmlFor={`option-${option.value}`}>{option.label}</label>
            </OptionItem>
          ))}
        </DropdownMenu>
      )}
    </Container>
  );
};

export default TableCustomCheckboxDropdownPill;
